/**
 * storyCanvasApi — REST fetch functions for the three canvas endpoints.
 *
 * Toggle via REACT_APP_STORY_CANVAS_API env flag:
 *   unset / "live" → real HTTP calls
 *   "mock"         → offline fixtures from storyCanvasApi.mock.ts
 *
 * All three functions are consumed by useStoryCanvasApi hook.
 *
 * The search and the writes after it take an optional `version`: api-service
 * answers them with the same contract for a Story on the new path (see
 * `ApiVersion`). The mocks ignore it.
 */
import { isMockApiFlag } from "../apiMode";
import { API_ENDPOINTS, apiService, type ApiVersion } from "../api";
import { pollEdit } from "../distributions/distributionEdits";
import {
  mockFetchCanvasManifest,
  mockFetchCanvasWidget,
  mockFetchCanvasDataset,
  mockFetchCanvasSearch,
  mockAddCanvasItem,
  mockUpdateCanvasItemsPositions,
  mockUpdateCanvasItemVisibility,
  mockUpdateCanvasItemStatus,
  mockCreateCanvasContentItem,
  mockUpdateCanvasContentItem,
  mockUpdateCanvasItemSource,
  mockFetchCanvasItemSummaries,
  mockFetchCanvasItem,
  mockPatchCanvasItem,
  mockDeleteCanvasItem,
} from "./storyCanvasApi.mock";
import type {
  CanvasAuthResponse,
  CanvasDatasetApi,
  CanvasItemBulkPositionResult,
  CanvasItemAddRequest,
  CanvasItemsBulkPositionUpdateRequest,
  CanvasItemStatusDecision,
  CanvasItemStatusUpdateRequest,
  CanvasItemStubApi,
  CanvasItemVisibilityUpdateRequest,
  CanvasManifestApi,
  CanvasSearchParams,
  CanvasSearchResponseApi,
  CanvasWidgetApi,
  CanvasContentItemApi,
  CanvasContentItemCreateRequestApi,
  CanvasContentItemUpdateRequestApi,
  CanvasItemSourceUpdateRequestApi,
  CanvasItemFullApi,
  CanvasItemListResponseApi,
  CanvasItemListTypeFilterApi,
  CanvasItemPatchRequestApi,
  StoryEditReadApi,
  ItemPositionApi,
} from "./storyCanvas.types";
import type { PlanLayout } from "../dataStories/storiesApi.types";

const isMock = (): boolean =>
  isMockApiFlag(process.env.REACT_APP_STORY_CANVAS_API);

/**
 * Its own flag, not REACT_APP_STORY_CANVAS_API: api-service doesn't serve the
 * v2 item list/GET/PATCH yet, so they can stay mocked while the v1 canvas runs
 * live. DELETE is served, and follows the canvas flag instead.
 */
const isMockStoryItems = (): boolean =>
  isMockApiFlag(process.env.REACT_APP_STORY_ITEMS_API);

// ─── Endpoint A — canvas manifest ─────────────────────────────────────────────

/**
 * Fetch the manifest for a story canvas.
 * Returns lightweight stubs (item_id, type, position) for every widget.
 * Use to seed skeleton placeholders before individual widget fetches complete.
 */
export async function fetchCanvasManifest(
  workspaceId: string,
  storyId: string,
): Promise<CanvasManifestApi> {
  if (isMock()) return mockFetchCanvasManifest(workspaceId, storyId);

  const envelope = await apiService.get<CanvasAuthResponse<CanvasManifestApi>>(
    API_ENDPOINTS.stories.canvasManifest(workspaceId, storyId),
  );
  return envelope.data;
}

// ─── Endpoint B — per-widget content ──────────────────────────────────────────

/**
 * Fetch the full content for a single canvas widget by its item_id.
 * For chart widgets the returned spec has empty data.values — call
 * fetchCanvasDataset() with the widget's item_id to get the rows.
 */
export async function fetchCanvasWidget(
  workspaceId: string,
  storyId: string,
  widgetId: string,
): Promise<CanvasWidgetApi> {
  if (isMock()) return mockFetchCanvasWidget(workspaceId, storyId, widgetId);

  const envelope = await apiService.get<CanvasAuthResponse<CanvasWidgetApi>>(
    API_ENDPOINTS.stories.canvasWidget(workspaceId, storyId, widgetId),
  );
  return envelope.data;
}

/**
 * Salesforce only. Unlike every other widget, Salesforce serves no data
 * captured at add-time — this re-fetches the report/dashboard fresh from
 * Salesforce's Reports & Dashboards REST API on every call. Returns the raw
 * Salesforce response (reportMetadata/factMap/groupingsDown for a report,
 * dashboardMetadata/componentData for a dashboard) — see
 * salesforceReportTransform.ts for turning this into chart-ready data.
 */
export async function fetchSalesforceWidgetData(
  workspaceId: string,
  storyId: string,
  widgetId: string,
): Promise<Record<string, unknown>> {
  const envelope = await apiService.get<CanvasAuthResponse<Record<string, unknown>>>(
    API_ENDPOINTS.stories.canvasWidgetSalesforceData(workspaceId, storyId, widgetId),
  );
  return envelope.data;
}

/**
 * Same live re-fetch as `fetchSalesforceWidgetData`, but with one or more
 * of the report's existing filters' values overridden — never saved back to
 * Salesforce, applied fresh for this one response. Hits the POST sibling of
 * the GET endpoint above, which stays untouched for the unfiltered path.
 */
export async function fetchSalesforceWidgetDataFiltered(
  workspaceId: string,
  storyId: string,
  widgetId: string,
  filters: Array<{ column: string; value: string }>,
): Promise<Record<string, unknown>> {
  const envelope = await apiService.post<CanvasAuthResponse<Record<string, unknown>>>(
    API_ENDPOINTS.stories.canvasWidgetSalesforceData(workspaceId, storyId, widgetId),
    { filters },
  );
  return envelope.data;
}

/** A single aggregated row from a HubSpot live-query result. */
export interface HubspotQueryResultRow {
  group: string;
  value: number;
}

export interface HubspotQueryDefinition {
  object_type: string;
  metric: "count" | "sum" | "avg";
  metric_property?: string | null;
  group_by: string;
  filters: Array<{ property: string; operator: string; value: string }>;
  /**
   * Purely a rendering choice — can't be auto-detected from HubSpot's data
   * API (chart type is a report-authoring setting, not data), unlike
   * Salesforce's live chart which reads its own reportMetadata.chart.
   */
  chart_type: "bar" | "line" | "pie";
}

export interface HubspotWidgetDataResponse {
  query: HubspotQueryDefinition;
  data: HubspotQueryResultRow[];
  row_count: number;
  truncated: boolean;
}

/**
 * HubSpot only, and only for widgets with a stored `hubspot_query`. Re-runs
 * that query fresh against HubSpot's CRM Search API on every call — see
 * hubspot_live_data_service.py. Additive to (never replaces) the widget's
 * `snapshot_svg` fallback.
 */
export async function fetchHubspotWidgetData(
  workspaceId: string,
  storyId: string,
  widgetId: string,
): Promise<HubspotWidgetDataResponse> {
  const envelope = await apiService.get<CanvasAuthResponse<HubspotWidgetDataResponse>>(
    API_ENDPOINTS.stories.canvasWidgetHubspotData(workspaceId, storyId, widgetId),
  );
  return envelope.data;
}

/** Sets/replaces a HubSpot widget's live-query definition. */
export async function setHubspotWidgetQuery(
  workspaceId: string,
  storyId: string,
  widgetId: string,
  query: HubspotQueryDefinition,
): Promise<void> {
  await apiService.patch<CanvasAuthResponse<null>>(
    API_ENDPOINTS.stories.canvasWidgetHubspotQuery(workspaceId, storyId, widgetId),
    query,
  );
}

// ─── Endpoint C — paginated chart dataset ─────────────────────────────────────

/**
 * Fetch the row-level dataset for a chart widget.
 * Pass the widget's item_id (the canvas item id), not data_id — the route is
 * scoped by canvas_item_id so the backend can verify the item belongs to
 * this story's canvas before returning its data.
 * Rows are keyed by the field names referenced in the Vega-Lite spec.
 */
export async function fetchCanvasDataset(
  workspaceId: string,
  storyId: string,
  canvasItemId: string,
  page = 1,
  pageSize = 100,
): Promise<CanvasDatasetApi> {
  if (isMock()) return mockFetchCanvasDataset(workspaceId, storyId, canvasItemId);

  const url =
    `${API_ENDPOINTS.stories.canvasDataset(workspaceId, storyId, canvasItemId)}` +
    `?page=${page}&page_size=${pageSize}`;

  const envelope =
    await apiService.get<CanvasAuthResponse<CanvasDatasetApi>>(url);
  return envelope.data;
}

// ─── Endpoint D — canvas search ────────────────────────────────────────────────

/**
 * Search connector assets + native canvas charts/kpis to add as a report.
 * Empty `q` returns all embeddable assets (backend default behaviour).
 */
export async function fetchCanvasSearch(
  workspaceId: string,
  params: CanvasSearchParams = {},
  version?: ApiVersion,
): Promise<CanvasSearchResponseApi> {
  const q = params.q ?? "";
  const types = params.types ?? [];
  const limit = params.limit ?? 20;

  if (isMock()) return mockFetchCanvasSearch(workspaceId, { q, types, limit });

  const envelope = await apiService.get<
    CanvasAuthResponse<CanvasSearchResponseApi>
  >(API_ENDPOINTS.stories.canvasSearch(workspaceId, version), {
    q,
    types: types.join(","),
    limit,
  });
  return envelope.data;
}

// ─── Add — persist a selected canvas-search result ───────────────────────────

/**
 * Add a searched connector asset/native item to the story at the exact grid
 * geometry chosen by the user. The backend returns the new manifest stub;
 * callers then load its full widget through the normal canvas widget pipeline.
 */
export async function addCanvasItem(
  workspaceId: string,
  storyId: string,
  body: CanvasItemAddRequest,
  version?: ApiVersion,
): Promise<CanvasItemStubApi> {
  if (isMock()) return mockAddCanvasItem(workspaceId, storyId, body);

  const envelope = await apiService.post<CanvasAuthResponse<CanvasItemStubApi>>(
    API_ENDPOINTS.stories.canvasItemsAdd(workspaceId, storyId, version),
    body,
  );
  return envelope.data;
}

// ─── Endpoint E — add a client-authored content item (link/embed/image/text) ──

/**
 * Add a client-authored content item (link, embed, image, or text) to the
 * canvas. Returns the full widget data (same shape fetchCanvasWidget
 * returns), so the caller can render it immediately without a follow-up GET.
 */
export async function createCanvasContentItem(
  workspaceId: string,
  storyId: string,
  body: CanvasContentItemCreateRequestApi,
  version?: ApiVersion,
): Promise<CanvasContentItemApi> {
  if (isMock()) return mockCreateCanvasContentItem(workspaceId, storyId, body);

  const envelope = await apiService.post<CanvasAuthResponse<CanvasContentItemApi>>(
    API_ENDPOINTS.stories.canvasContentItemAdd(workspaceId, storyId, version),
    body,
  );
  return envelope.data;
}

// ─── Patch — bulk move canvas items ───────────────────────────────────────────

/**
 * Bulk-update canvas item geometry (1 or N).
 * Body uses `pos_x` / `pos_y` and optional `pos_w` / `pos_h`.
 * Callers keep layout in local state — do not refetch manifest/widgets.
 * This is the only position endpoint (per-item `/position` does not exist).
 */
export async function updateCanvasItemsPositions(
  workspaceId: string,
  storyId: string,
  items: CanvasItemsBulkPositionUpdateRequest["items"],
  version?: ApiVersion,
): Promise<CanvasItemStubApi[]> {
  if (items.length === 0) return [];

  if (isMock())
    return mockUpdateCanvasItemsPositions(workspaceId, storyId, items);

  const body: CanvasItemsBulkPositionUpdateRequest = { items };
  const envelope = await apiService.patch<
    CanvasAuthResponse<CanvasItemBulkPositionResult[]>
  >(
    API_ENDPOINTS.stories.canvasItemsUpdatePosition(workspaceId, storyId, version),
    body,
  );

  return envelope.data.map((row) => ({
    item_id: row.item_id,
    type: row.type,
    position: row.position,
    status: row.status ?? null,
  }));
}

// ─── Patch — content visibility (summary / narrative show flags) ──────────────

/**
 * Toggle one artifact's summary/narrative visibility on a canvas item.
 * Insert AI summary/narrative on a chart PATCHes flags true (no generate).
 * Returns the updated stub (includes live `visibility` map).
 */
export async function updateCanvasItemVisibility(
  workspaceId: string,
  storyId: string,
  itemId: string,
  body: CanvasItemVisibilityUpdateRequest,
  version?: ApiVersion,
): Promise<CanvasItemStubApi> {
  if (isMock())
    return mockUpdateCanvasItemVisibility(workspaceId, storyId, itemId, body);

  const envelope = await apiService.patch<
    CanvasAuthResponse<CanvasItemStubApi>
  >(
    API_ENDPOINTS.stories.canvasItemContentVisibility(
      workspaceId,
      storyId,
      itemId,
      version,
    ),
    body,
  );
  return envelope.data;
}

// ─── Patch — accept / reject a chat-added canvas item ─────────────────────────

/**
 * Accept or reject a chat-added canvas item. `accepted` confirms it (renders
 * normally); `rejected` removes it. Callers refetch the manifest so the canvas
 * reflects the decision (rejected items drop off).
 */
export async function updateCanvasItemStatus(
  workspaceId: string,
  storyId: string,
  itemId: string,
  status: CanvasItemStatusDecision,
  version?: ApiVersion,
): Promise<CanvasItemStubApi> {
  if (isMock())
    return mockUpdateCanvasItemStatus(workspaceId, storyId, itemId, status);

  const body: CanvasItemStatusUpdateRequest = { status };
  const envelope = await apiService.patch<
    CanvasAuthResponse<CanvasItemStubApi>
  >(
    API_ENDPOINTS.stories.canvasItemStatusUpdate(
      workspaceId,
      storyId,
      itemId,
      version,
    ),
    body,
  );
  return envelope.data;
}

// ─── Patch — update a client-authored content item's content/metadata ────────

/**
 * Update a link/embed/image/text item's content/metadata (e.g. edit the URL,
 * embed HTML, image, or text body). Returns the full widget data, same shape
 * fetchCanvasWidget returns, so the caller can re-render immediately without
 * a follow-up GET.
 */
export async function updateCanvasContentItem(
  workspaceId: string,
  storyId: string,
  itemId: string,
  body: CanvasContentItemUpdateRequestApi,
  version?: ApiVersion,
): Promise<CanvasContentItemApi> {
  if (isMock())
    return mockUpdateCanvasContentItem(workspaceId, storyId, itemId, body);

  const envelope = await apiService.patch<
    CanvasAuthResponse<CanvasContentItemApi>
  >(
    API_ENDPOINTS.stories.canvasContentItemUpdate(
      workspaceId,
      storyId,
      itemId,
      version,
    ),
    body,
  );
  return envelope.data;
}

// ─── Patch — re-point a report/ingested_widget/captured_widget item's source ──

/**
 * Re-point a report/ingested_widget/captured_widget item at a different
 * connector asset — the "Edit" action that reselects a chart/sheet/visual
 * from the connected BI tool. Returns the full widget data, same shape
 * fetchCanvasWidget returns.
 */
export async function updateCanvasItemSource(
  workspaceId: string,
  storyId: string,
  itemId: string,
  body: CanvasItemSourceUpdateRequestApi,
  version?: ApiVersion,
): Promise<CanvasWidgetApi> {
  if (isMock())
    return mockUpdateCanvasItemSource(workspaceId, storyId, itemId, body);

  const envelope = await apiService.patch<CanvasAuthResponse<CanvasWidgetApi>>(
    API_ENDPOINTS.stories.canvasItemSourceUpdate(
      workspaceId,
      storyId,
      itemId,
      version,
    ),
    body,
  );
  return envelope.data;
}

// ─── Canvas items (v2) ────────────────────────────────────────────────────────
// Flat under the story and unwrapped: these return the bare body, unlike the
// CanvasAuthResponse-enveloped v1 calls above.

/**
 * `?detail=summary`: one stub per item with its computed `editable`. That is
 * all the Edit gate needs, without compiling every chart's spec.
 */
export async function fetchCanvasItemSummaries(
  storyId: string,
  type?: CanvasItemListTypeFilterApi,
): Promise<CanvasItemListResponseApi> {
  if (isMockStoryItems()) return mockFetchCanvasItemSummaries(storyId, type);

  return apiService.get<CanvasItemListResponseApi>(
    API_ENDPOINTS.stories.items(storyId),
    type ? { detail: "summary", type } : { detail: "summary" },
  );
}

/** One item in full: `binding`, `format`, `query_model` and a compiled `spec`. */
export async function fetchCanvasItem(
  storyId: string,
  itemId: string,
): Promise<CanvasItemFullApi> {
  if (isMockStoryItems()) return mockFetchCanvasItem(storyId, itemId);

  return apiService.get<CanvasItemFullApi>(API_ENDPOINTS.stories.item(storyId, itemId));
}

/** One line of the batch read: the item exactly as the single GET answers it, or why not. */
export type CanvasItemBatchLine =
  | { id: string; item: CanvasItemFullApi; error?: undefined }
  | { id: string; item?: undefined; error: { code: string; message: string } };

/**
 * Several items in one call, `onLine` for each as its line arrives (any order). Rejects when the
 * call itself fails (an old backend's 404, the network); a single item's failure is its own line.
 */
export async function fetchCanvasItemsBatch(
  storyId: string,
  itemIds: readonly string[],
  onLine: (line: CanvasItemBatchLine) => void,
  signal?: AbortSignal,
): Promise<void> {
  await apiService.streamLines<CanvasItemBatchLine>(
    API_ENDPOINTS.stories.itemsBatch(storyId, itemIds),
    onLine,
    signal,
  );
}

/**
 * Merge-patch `binding` and/or `format`, sending only what changed but
 * `binding.y` in full. Responds with the item and a freshly compiled `spec`.
 * The server rejects a legacy item with a 409.
 */
export async function patchCanvasItem(
  storyId: string,
  itemId: string,
  body: CanvasItemPatchRequestApi,
): Promise<CanvasItemFullApi> {
  if (isMockStoryItems()) return mockPatchCanvasItem(storyId, itemId, body);

  return apiService.patch<CanvasItemFullApi>(
    API_ENDPOINTS.stories.item(storyId, itemId),
    body,
  );
}

/**
 * Soft delete (204, no body) of any canvas item, reports and content items
 * included: the server marks the `story_canvas_items` row deleted, and callers
 * refetch the v1 manifest so the widget drops off the canvas.
 *
 * Gated on the v1 canvas flag, not REACT_APP_STORY_ITEMS_API: api-service
 * serves this route while the other item routes stay mocked, and the ids it
 * deletes come from the v1 manifest, so a mock manifest's ids only exist in
 * the mock (and a live manifest's only on the server).
 */
export async function deleteCanvasItem(
  storyId: string,
  itemId: string,
): Promise<void> {
  if (isMock()) return mockDeleteCanvasItem(storyId, itemId);

  await apiService.delete<void>(API_ENDPOINTS.stories.item(storyId, itemId));
}

/**
 * Post one Story edit (`{ edit: { kind, … } }`). Answers the edit's request id,
 * to poll with `fetchStoryEdit` / `waitStoryEdit`.
 */
export async function postStoryEdit(
  storyId: string,
  edit: { kind: string } & Record<string, unknown>,
): Promise<string> {
  const { request_id: requestId } = await apiService.post<{ request_id: string }>(
    API_ENDPOINTS.stories.edits(storyId),
    { edit },
  );
  return requestId;
}

/**
 * A new-path text item written again from the Story's current facts: the
 * `rewrite_text` edit. Answers the edit's request id, to poll with
 * `fetchStoryEdit`.
 */
export async function requestTextRewrite(
  storyId: string,
  itemId: string,
): Promise<string> {
  return postStoryEdit(storyId, { kind: "rewrite_text", item_id: itemId });
}

/** The layout a new-path Story's canvas is in. */
export async function fetchStoryLayout(storyId: string): Promise<PlanLayout> {
  const story = await apiService.get<{ layout: PlanLayout }>(API_ENDPOINTS.stories.newPathStory(storyId));
  return story.layout;
}

/**
 * The canvas put in another layout: the `set_layout` edit. Items move to the
 * layout's places and text for its new places is written after. Answers the
 * edit's request id, to poll with `fetchStoryEdit`.
 */
export async function requestLayoutSwitch(storyId: string, layout: PlanLayout): Promise<string> {
  return postStoryEdit(storyId, { kind: "set_layout", layout });
}

export async function fetchStoryEdit(
  storyId: string,
  requestId: string,
): Promise<StoryEditReadApi> {
  return apiService.get<StoryEditReadApi>(
    API_ENDPOINTS.stories.edit(storyId, requestId),
  );
}

/** Post an edit and wait until Story Manager says it is done; rejects with the edit's own message when it failed. */
export async function waitStoryEdit(storyId: string, requestId: string): Promise<StoryEditReadApi> {
  return pollEdit(() => fetchStoryEdit(storyId, requestId));
}

/**
 * Bring back an item that was deleted (unpinned): accept it, then put it at its
 * old geometry. On the new path an unpin keeps the canvas row, so the same id
 * comes back; the pin lands at the bottom first, hence the second call.
 */
export async function repinCanvasItem(
  workspaceId: string,
  storyId: string,
  itemId: string,
  position: ItemPositionApi | null,
  version?: ApiVersion,
): Promise<void> {
  await updateCanvasItemStatus(workspaceId, storyId, itemId, "accepted", version);
  if (!position) return;
  await updateCanvasItemsPositions(
    workspaceId,
    storyId,
    [positionUpdateFor(itemId, position)],
    version,
  );
}

/** The bulk-position row that puts `itemId` at `position`, with every field explicit. */
export const positionUpdateFor = (
  itemId: string,
  position: ItemPositionApi,
): CanvasItemsBulkPositionUpdateRequest["items"][number] => ({
  item_id: itemId,
  pos_x: position.x,
  pos_y: position.y,
  pos_w: position.w,
  pos_h: position.h,
  ...(position.cols != null ? { pos_cols: position.cols } : {}),
  pos_h_fixed: position.h_fixed ?? false,
});
